import React, { useState } from 'react';
import { X, Mail } from 'lucide-react';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { db, auth } from '../../firebase';
import toast from 'react-hot-toast';

interface InviteTeamMemberModalProps {
  onClose: () => void;
}

export default function InviteTeamMemberModal({ onClose }: InviteTeamMemberModalProps) {
  const [emails, setEmails] = useState('');
  const [department, setDepartment] = useState('');
  const [role, setRole] = useState<'admin' | 'staff'>('staff');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const emailList = emails.split(',').map(email => email.trim()).filter(Boolean);
    if (emailList.length === 0) {
      toast.error('Please enter at least one email address');
      return;
    }

    try {
      setLoading(true);
      const user = auth.currentUser;
      if (!user) return;

      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + 7); // Expires in 7 days

      // Create invites for each email
      await Promise.all(
        emailList.map(async (email) => {
          await addDoc(collection(db, 'invites'), {
            email,
            role,
            department: department.trim() || undefined,
            status: 'pending',
            createdAt: serverTimestamp(),
            expiresAt: expiresAt.toISOString(),
            invitedBy: user.uid
          });
        })
      );

      toast.success(`Invitations sent to ${emailList.length} recipient(s)`);
      onClose();
    } catch (error) {
      console.error('Error sending invites:', error);
      toast.error('Failed to send invitations');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-surface-light rounded-xl shadow-lg w-full max-w-md mx-4">
        <div className="flex items-center justify-between p-6 border-b border-border-light">
          <h2 className="text-xl font-semibold text-text-primary">Invite Team Members</h2>
          <button
            onClick={onClose}
            className="text-text-secondary hover:text-text-primary transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label htmlFor="emails" className="block text-sm font-medium text-text-secondary mb-1">
              Email Addresses
            </label>
            <textarea
              id="emails"
              value={emails}
              onChange={(e) => setEmails(e.target.value)}
              className="w-full px-4 py-2 rounded-lg border border-border-light focus:border-primary-500 
                       focus:ring-2 focus:ring-primary-500/20 outline-none transition-all"
              placeholder="Enter email addresses, separated by commas"
              rows={3}
              required
            />
            <p className="mt-1 text-xs text-text-secondary">
              You can invite multiple team members by separating their email addresses with commas.
            </p>
          </div>

          <div>
            <label htmlFor="department" className="block text-sm font-medium text-text-secondary mb-1">
              Department (Optional)
            </label>
            <input
              id="department"
              type="text"
              value={department}
              onChange={(e) => setDepartment(e.target.value)}
              className="w-full px-4 py-2 rounded-lg border border-border-light focus:border-primary-500 
                       focus:ring-2 focus:ring-primary-500/20 outline-none transition-all"
              placeholder="e.g., Science Department"
            />
          </div>

          <div>
            <label htmlFor="role" className="block text-sm font-medium text-text-secondary mb-1">
              Role
            </label>
            <select
              id="role"
              value={role}
              onChange={(e) => setRole(e.target.value as 'admin' | 'staff')}
              className="w-full px-4 py-2 rounded-lg border border-border-light focus:border-primary-500 
                       focus:ring-2 focus:ring-primary-500/20 outline-none transition-all"
            >
              <option value="staff">Staff Member</option>
              <option value="admin">Administrator</option>
            </select>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-text-secondary hover:text-text-primary transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 
                       transition-colors disabled:opacity-50 disabled:cursor-not-allowed 
                       flex items-center gap-2"
            >
              <Mail className="w-5 h-5" />
              {loading ? 'Sending...' : 'Send Invitations'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}